import { Popover } from 'lib/components/Popover';
import { ColumnButton } from 'lib/components/ColumnButton';
import { FunnelIcon } from '@heroicons/react/24/outline';
import { AdjudicationAreaWithId } from 'lib/types/adjudicationArea';
import { SelectStateDropdown } from './SelectStateDropdown';
import { PublisherLocationFilterBadge } from './PublisherLocationFilterBadge';
import { PublisherLocationFilterFormAdjudicated } from './adjudicated/PublisherLocationFilterFormAdjudicated';
import {
  AdjudicationFilters,
  PublisherFilters
} from '../helpers/publisherSearch';

type PublisherLocationFilterProps = {
  stateOptions: number[];
  onStateChange: (stateId?: number) => void;
  onAdjudicationFilterChange?: (filters: AdjudicationFilters) => void;
  activeFilters: PublisherFilters;
};

export function PublisherLocationFilter({
  stateOptions,
  onStateChange,
  onAdjudicationFilterChange,
  activeFilters
}: PublisherLocationFilterProps) {
  const activeFilterCount = getLocationFilterCount(activeFilters);

  function resetFilters() {
    onStateChange(undefined);
    onAdjudicationFilterChange &&
      onAdjudicationFilterChange({
        stateFilter: undefined,
        countyFilter: undefined,
        cityFilter: undefined
      });
  }

  return (
    <Popover
      id="publisher-location-filter"
      alignment="right"
      activator={
        <ColumnButton
          link
          secondary
          buttonText={
            <>
              Filter location{' '}
              <PublisherLocationFilterBadge
                activeFilterCount={activeFilterCount}
                onClear={resetFilters}
              />
            </>
          }
          startIcon={<FunnelIcon className="pb-1 pt-0.5 w-5" />}
          type="button"
        />
      }
    >
      <div className="p-4 space-y-4 w-80">
        <PublisherLocationFilterForm
          stateOptions={stateOptions}
          selectedState={activeFilters.locationFilter.stateFilter}
          onStateChange={onStateChange}
        />
        {onAdjudicationFilterChange && (
          <>
            <hr />
            <PublisherLocationFilterFormAdjudicated
              selectedState={activeFilters.adjudicationFilter?.stateFilter}
              selectedCounty={activeFilters.adjudicationFilter?.countyFilter}
              selectedCity={activeFilters.adjudicationFilter?.cityFilter}
              onSelectionChanged={function (
                dropdown: 'state' | 'county' | 'city',
                value: AdjudicationAreaWithId
              ) {
                const newFilters: AdjudicationFilters = {
                  ...activeFilters.adjudicationFilter,
                  [`${dropdown}Filter`]: value
                };
                onAdjudicationFilterChange(newFilters);
              }}
            />
          </>
        )}
        <ColumnButton
          disabled={activeFilterCount === 0}
          buttonText="Reset"
          onClick={resetFilters}
          type="button"
        />
      </div>
    </Popover>
  );
}

type PublisherLocationFilterFormProps = {
  stateOptions: number[];
  selectedState: number | undefined;
  onStateChange: (stateId?: number) => void;
};

function PublisherLocationFilterForm({
  stateOptions,
  selectedState,
  onStateChange
}: PublisherLocationFilterFormProps) {
  return (
    <fieldset>
      <legend className="text-sm font-medium mb-2">Filter by location</legend>
      <SelectStateDropdown
        selectedState={selectedState ? String(selectedState) : ''}
        stateOptions={stateOptions}
        onChange={stateId => {
          onStateChange(stateId ? Number(stateId) : undefined);
        }}
      />
    </fieldset>
  );
}

export function getLocationFilterCount(filters: PublisherFilters) {
  // NOTE: Extend this calculation if we add more filters
  return [
    filters.locationFilter.stateFilter,
    filters.adjudicationFilter?.stateFilter,
    filters.adjudicationFilter?.countyFilter,
    filters.adjudicationFilter?.cityFilter
  ].filter(filter => !!filter).length;
}
